import { useMemo, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Snackbar } from '@scramble/ui';
import { useModal } from '@/providers/modal';
import { LOAN_GROUPS_IDS } from '@/helpers/constants';
import { safeDecimal } from '@/helpers/number';
import { prettyFloatMoney } from '@/helpers/utils';
import { useWindowSize } from '@/hooks/useWindowSize';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { ModalSwipeLayout } from '@/kit/Modal/layouts/swipe';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { Preloader } from '@/components/Preloader';
import { Image } from '@/components/Image';
import { useBrandSelectionStatus } from '@/pages/InvestingPage/hooks/useBrandSelectionStatus';
import { useSaveBrandsForInvestment } from '@/query/queries/investing/useSaveBrandsForInvestment';
import IconInfo from '@scramble/design-system/icons/info-circle.svg';
import IconChevronDown from '@scramble/design-system/icons/chevron-down.svg';
import s from './index.module.scss';

const id = MODAL_IDS.new_brands_joined_round;

const SHOW_BRANDS_COUNT = 5;

export const NewBrandsJoinedRoundModal = props => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal {...props} />;
};

const Modal = ({ groups = [], onCancel, onContinue }) => {
  const [showAllBrands, setShowAllBrands] = useState(false);
  const { isMobile } = useWindowSize();
  const { closeModal } = useModal();
  const { t } = useTranslation('modal');
  const { mutateAsync: saveBrands, isPending } = useSaveBrandsForInvestment();
  const { hasData, newBrandList, selectedBrandList } =
    useBrandSelectionStatus();

  const newBrands = useMemo(() => {
    const groupIds = groups.map(item => item.group);
    const brandMap = new Map();

    newBrandList.forEach(brand => {
      const hasCapacity = groupIds.some(group =>
        safeDecimal(brand.capacity?.[group]).greaterThan(0),
      );

      if (hasCapacity) {
        brandMap.set(brand.id, brand);
      }
    });

    return Array.from(brandMap.values());
  }, [groups, newBrandList]);

  const visibleBrands = showAllBrands
    ? newBrands
    : newBrands.slice(0, SHOW_BRANDS_COUNT);
  const hiddenBrandsCount = newBrands.length - visibleBrands.length;
  const infoGroups = groups.filter(item => item.amount);
  const infoAmount = infoGroups
    .reduce((sum, item) => sum.add(safeDecimal(item.amount)), safeDecimal(0))
    .toNumber();

  const groupLabel = group => {
    if (group === LOAN_GROUPS_IDS.conservative.id) {
      return t('new_brands_joined_round_modal.group_a');
    }

    return t('new_brands_joined_round_modal.group_b');
  };
  const infoGroupLabel =
    infoGroups.length > 1
      ? t('new_brands_joined_round_modal.groups_a_b')
      : groupLabel(infoGroups[0]?.group);

  const handleCancel = () => {
    closeModal(id);
    onCancel?.();
  };

  const handleAddAndContinue = async () => {
    const payload = Array.from(
      new Set([
        ...selectedBrandList.map(brand => brand.id),
        ...newBrands.map(brand => brand.id),
      ]),
    );

    try {
      const res = await saveBrands(payload);

      if (res?.list) {
        const completed = await onContinue?.();

        if (completed !== false) {
          closeModal(id);
        }
      }
    } catch (error) {
      Snackbar({
        type: 'danger',
        text: error?.message || t('new_brands_joined_round_modal.error'),
      });
    }
  };

  const content = (
    <div className={s.modal}>
      <div className={s.header}>
        <div className={s.header_icon}>
          <img src={IconInfo} alt="info" width={28} height={28} />
        </div>
        <h1 className="f-headline-2xs f-500">
          {t('new_brands_joined_round_modal.title')}
        </h1>
        <p className="f-body-sm">
          {t('new_brands_joined_round_modal.description')}
        </p>
      </div>

      <div className={s.content}>
        {!hasData ? (
          <Preloader />
        ) : (
          <>
            <div className={s.content_scroll}>
              <div className={s.brands}>
                {visibleBrands.map((item, index) => {
                  const isLastVisibleBrand = index === visibleBrands.length - 1;
                  const hideDivider =
                    hiddenBrandsCount > 0 && isLastVisibleBrand;

                  return (
                    <div
                      key={item.id}
                      className={classnames(
                        s.brand,
                        hideDivider && s.brand_no_divider,
                      )}
                    >
                      <div className={s.brand_content}>
                        <div className={s.brand_icon}>
                          <Image
                            image={item.logo}
                            preset="selectionLogo"
                            alt={item.name}
                            width={24}
                            height={24}
                          />
                        </div>
                        <div className={classnames('f-body-md', s.brand_name)}>
                          {item.name}
                        </div>
                      </div>
                      <Badge
                        type="success"
                        appearance="filled"
                        size="small"
                        label={t('new_brands_joined_round_modal.new')}
                      />
                    </div>
                  );
                })}

                {hiddenBrandsCount > 0 && (
                  <button
                    type="button"
                    className={classnames(s.more, 'f-body-sm f-500')}
                    onClick={() => setShowAllBrands(true)}
                  >
                    {t('new_brands_joined_round_modal.show_more_brands', {
                      count: hiddenBrandsCount,
                    })}
                    <img src={IconChevronDown} alt="" width={16} height={16} />
                  </button>
                )}
              </div>
            </div>

            {!!infoGroups.length && (
              <div className={s.warning}>
                <img
                  src={IconInfo}
                  alt="info"
                  width={20}
                  height={20}
                  style={{ minWidth: '20px' }}
                />
                <div className={s.warning_text}>
                  <p className="f-body-sm">
                    {t('new_brands_joined_round_modal.info_line', {
                      amount: prettyFloatMoney(infoAmount),
                      group: infoGroupLabel,
                    })}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className={s.footer}>
        <Button
          type="button"
          theme="neutral"
          appearance="tonal"
          size="lg"
          onClick={handleCancel}
        >
          {t('new_brands_joined_round_modal.cancel')}
        </Button>
        <Button
          type="button"
          theme="neutral"
          appearance="filled"
          size="lg"
          disabled={!hasData || !newBrands.length || isPending}
          onClick={handleAddAndContinue}
        >
          {t('new_brands_joined_round_modal.add_continue')}
        </Button>
      </div>
    </div>
  );

  return isMobile ? (
    <ModalSwipeLayout id={id} onClose={handleCancel} showClose>
      {content}
    </ModalSwipeLayout>
  ) : (
    <ModalCenterLayout id={id} onClose={handleCancel} showClose>
      {content}
    </ModalCenterLayout>
  );
};
